import React, { useState } from "react";
import { ObjectiveEditor, ConfirmDialog } from "./";
import type { ObjectivesListProps } from "../objectivesPhase.types";

export default function ObjectivesList({ objectives, onUpdate, isGenerating }: ObjectivesListProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    index: number;
    objectiveText: string;
  }>({
    isOpen: false,
    index: -1,
    objectiveText: ""
  });

  const handleUpdate = (index: number, value: string) => {
    const updatedObjectives = [...objectives];
    updatedObjectives[index] = value;
    onUpdate(updatedObjectives);
  };

  const handleDelete = (index: number) => {
    const objectiveText = objectives[index];
    setConfirmDelete({
      isOpen: true,
      index,
      objectiveText: objectiveText.length > 50 ? objectiveText.substring(0, 50) + "..." : objectiveText
    });
  };

  const confirmDeleteObjective = () => {
    const { index } = confirmDelete;
    const updatedObjectives = objectives.filter((_, i) => i !== index);
    onUpdate(updatedObjectives);
    
    // Adjust editing index if needed
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }

    setConfirmDelete({ isOpen: false, index: -1, objectiveText: "" });
  };

  const cancelDelete = () => {
    setConfirmDelete({ isOpen: false, index: -1, objectiveText: "" });
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleSave = (index: number) => {
    setEditingIndex(null);
  };

  const handleCancel = (index: number) => {
    setEditingIndex(null);
    // If objective is empty, remove it
    if (!objectives[index]?.trim()) {
      handleDelete(index);
    }
  };

  // Auto-edit empty objectives
  React.useEffect(() => {
    const lastIndex = objectives.length - 1;
    if (lastIndex >= 0 && !objectives[lastIndex]?.trim() && editingIndex !== lastIndex) {
      setEditingIndex(lastIndex);
    }
  }, [objectives, editingIndex]);

  return (
    <div className="space-y-4">
      {objectives.map((objective, index) => (
        <ObjectiveEditor
          key={index}
          objective={objective}
          index={index}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          isEditing={editingIndex === index}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ))}

      {objectives.length === 0 && (
        <div className="text-center py-8">
          <p className="text-raro-gray-500">
            Nenhum objetivo adicionado ainda.
          </p>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title="Excluir Objetivo"
        message={`Tem certeza que deseja excluir este objetivo?\n\n"${confirmDelete.objectiveText}"`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDeleteObjective}
        onCancel={cancelDelete}
        type="danger"
      />
    </div>
  );
}